import { type AppInfo } from 'api/types/AppInfo';
import { type ChapterContentPage } from 'api/types/ChapterContent/ChapterContentPage';
import { type QuestionComboLock } from 'api/types/ChapterContent/QuestionComboLock';
import { type RawQuestionComboLock } from 'api/types/ChapterContent/QuestionComboLock/RawQuestionComboLock';
import { type QuestionFillInTheBlanks } from 'api/types/ChapterContent/QuestionFillInTheBlanks';
import { type RawQuestionFillInTheBlanks } from 'api/types/ChapterContent/QuestionFillInTheBlanks/RawQuestionFillInTheBlanks';
import { type QuestionFlipboard } from 'api/types/ChapterContent/QuestionFlipboard';
import { type RawQuestionFlipboard } from 'api/types/ChapterContent/QuestionFlipboard/RawQuestionFlipboard';
import { type QuestionMultipleChoice } from 'api/types/ChapterContent/QuestionMultipleChoice';
import { type RawQuestionMultipleChoice } from 'api/types/ChapterContent/QuestionMultipleChoice/RawQuestionMultipleChoice';
import { type QuestionSlider } from 'api/types/ChapterContent/QuestionSlider';
import { type RawQuestionSlider } from 'api/types/ChapterContent/QuestionSlider/RawQuestionSlider';

import { parseBlanks } from './questions/parseBlanks';
import { parseComboLock } from './questions/parseComboLock';
import { parseMultipleChoice } from './questions/parseMultipleChoice';
import { parseSlider } from './questions/parseSlider';

export type ChapterQuestionPage =
  | QuestionComboLock
  | QuestionFillInTheBlanks
  | QuestionFlipboard
  | QuestionMultipleChoice
  | QuestionSlider;

export type RawChapterQuestionPage =
  | RawQuestionComboLock
  | RawQuestionFillInTheBlanks
  | RawQuestionFlipboard
  | RawQuestionMultipleChoice
  | RawQuestionSlider;

export const parseQuestionData = (data: RawChapterQuestionPage & { image: string }, app: AppInfo) => {
  const parsedData: Partial<ChapterQuestionPage> = { image: data.image, title: data.question };

  switch (data.type) {
    case 'QuestionMultipleChoiceImage':
    case 'QuestionMultipleChoice':
      Object.assign(parsedData, parseMultipleChoice(data as RawQuestionMultipleChoice));
      break;

    case 'QuestionComboLock':
      Object.assign(parsedData, parseComboLock(data as RawQuestionComboLock));
      break;

    case 'stepsNormal':
      Object.assign(parsedData, parseSlider(data as RawQuestionSlider | RawQuestionFlipboard));
      break;

    case 'QuestionFillInTheBlank':
      Object.assign(parsedData, parseBlanks(data as RawQuestionFillInTheBlanks));
      break;

    default:
      // TODO: Replace with AppException (7348752934)
      throw new Error(`Unknown question type: ${(data as { type: string }).type} for question with id: ${app.id}`);
  }

  return parsedData as ChapterQuestionPage;
};

export const isQuestion = (data: ChapterContentPage | ChapterQuestionPage): data is ChapterQuestionPage => {
  return (data as ChapterQuestionPage).question !== undefined;
};
